import React, { useState, useEffect, useRef } from 'react';
import Api from '../../../services/Api';
import { DetailsList, DetailsListLayoutMode, IColumn, SelectionMode, ConstrainMode, ColumnActionsMode, IconButton, Stack, Spinner, SpinnerSize, Label } from '@fluentui/react';
import TextFilter from '../Filters/TextFilter';
import DropdownTree from '../../CustomComponents/DropdownTree';
import PageNavi from '../PageNavi';
import ResultsOnPage from '../ResultsOnPage';
import axios from 'axios';
import DropdownFilter from '../Filters/DropdownFilter';
import ExportButton from '../ExportButton';
import { PowerAppsScope, PowerAppStatus, PowerAppProvisioningStatus } from '../../../GlobalConst/globalEnums';
import { createAndDownloadCsv, getGroupedItems, mapBooleanToFilterValues, mapEnumToFilterValues, renderBoolean, renderDate, renderGeography, renderManagement, renderUser } from '../../../helpers/HelperMethods';
import SolutionActionButtonsView from './SolutionActionButtonsView';
import InfoTooltip from '../../formFields/InfoTooltip';

const CancelToken = axios.CancelToken;

const PowerPlatformSolutionsList = ({filterBy}) => {

    const apiAddress = '/PowerAppSolutionsPanel';

    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [powerApps, setPowerApps] = useState([]);
    const [orderBy, setOrderBy] = useState({ fieldName: '', isDescending: false});
    const [totalResults, setTotalResults] = useState(0);
    const [onPage, setOnPage] = useState(20);
    const [pageUrlFilter, setPageUrlFilter] = useState(1);
    const [filters, setFilters] = useState({});
    const [allTotalResults, setAllTotalResults] = useState(0)
    const cancelSource = useRef(null);

    const isOwnerView = filterBy === 'owner'

    useEffect(() => {
        return updateResults();
    }, [onPage, pageUrlFilter, filters, orderBy])

    const _onColumnClick = (ev, column) => {
        setOrderBy({
            fieldName: column.fieldName, 
            isDescending: !column.isSortedDescending
        });
    }

    const loadApps = (apps) => {
        setPowerApps(apps);
        setIsLoading(false);
    }

    const onFilterChange = (fieldName, value) => {
        setFilters({
            ...filters,
            [fieldName]: value
        });
        setPageUrlFilter(1);
    }

    const handleNoSelection = () => {
        loadApps([]);
        setTotalResults(0);
    }

    const getResultsUrl = (isExport) => {
        let url = `${apiAddress}?`
        
        if (isExport)
            url += `forExport=1`;
        else
            url += `page=${pageUrlFilter}&onPage=${onPage}`;

        const filterUrl = Object.keys(filters).reduce((queryString, filterField) => {
            const filterText = filters[filterField];
            if(filterText) {
                queryString += `&${filterField}=${encodeURIComponent(filterText)}`;
            }
            return queryString;
        }, '');

        let orderByUrl = '';
        if(orderBy.fieldName) {
            orderByUrl = `&orderBy=${orderBy.fieldName}&isDesc=${orderBy.isDescending}`;
        }

        url = url + filterUrl + orderByUrl;

        if (filterBy) {
            url += `&filterBy=${filterBy}`
        }

        return url
    }

    const updateResults = () => {
        setIsLoading(true);

        if (cancelSource.current) {
            cancelSource.current.cancel();
        }

        cancelSource.current = CancelToken.source();

        Api.get(getResultsUrl(false), {
            cancelToken: cancelSource.current.token
        })
        .then(res => res.data)
        .then(posts => {
            loadApps(posts.results);
            setTotalResults(posts.totalCount);
            setAllTotalResults(posts.allTotalCount);
        })
        .catch(err => {});

        return () => {
            cancelSource.current.cancel();
        }
    }

    // const renderWithTooltip = (content, stringContent) => {
    //     return  <TooltipHost content={stringContent ? stringContent : content} overflowMode={TooltipOverflowMode.Parent} closeDelay={500}>
    //                 <span>{content}</span>
    //             </TooltipHost>;
    // }

    const initialColumns: IColumn[] = [
        {
            key: 'name',
            name: 'Name',
            fieldName: 'name',
            minWidth: 150,
            maxWidth: 250,
            isRowHeader: false,
            isResizable: true,
            data: 'string'
        },
        {
            key: 'publisher',
            name: 'Publisher',
            fieldName: 'publisher',
            minWidth: 150,
            maxWidth: 250,
            isRowHeader: false,
            isResizable: true,
            data: 'string'
        },
        {
            key: 'environment',
            name: 'Environment',
            fieldName: 'environment',
            minWidth: 80,
            maxWidth: 150,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => <span>{item.environment.name}</span>
        },
        {
            key: 'isManaged',
            name: 'Is Managed Solution',
            fieldName: 'isManagedSolution',
            minWidth: 60,
            maxWidth: 80,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderBoolean(item.isManagedSolution)
        },
        {
            key: 'primaryOwner',
            name: 'Primary Owner',
            fieldName: 'primaryOwner',
            minWidth: 160,
            maxWidth: 200,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderUser(item.primaryOwner),
        },
        {
            key: 'secondaryOwner',
            name: 'Secondary Owner',
            fieldName: 'secondaryOwner',
            minWidth: 160,
            maxWidth: 200,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderUser(item.secondaryOwner),
        },
        {
            key: 'management',
            name: 'Organization',
            fieldName: 'management',
            minWidth: 50,
            maxWidth: 100,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderManagement(item.management)
        },
        {
            key: 'geography',
            name: 'Country',
            fieldName: 'geography',
            minWidth: 100,
            maxWidth: 150,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderGeography(item.geography)
        },
        {
            key: 'scope',
            name: 'Scope',
            fieldName: 'scope',
            minWidth: 60,
            maxWidth: 150,
            isResizable: true,
            data: 'string'
        },
        {
            key: 'status',
            name: 'Status',
            fieldName: 'status',
            minWidth: 60,
            maxWidth: 150,
            isResizable: true,
            data: 'string'
        },
        {
            key: 'provisioningStatus',
            name: 'Technical Status',
            fieldName: 'provisioningStatus',
            minWidth: 60,
            maxWidth: 150,
            isResizable: true,
            data: 'string'
        },
        {
            key: 'ardoq',
            name: 'Business Capability',
            fieldName: 'ardoq',
            minWidth: 100,
            maxWidth: 150,
            isResizable: true,
            data: 'string',
            onRender: (item) => {
                const value = item.ardoq.map(x => x.value).join(', ')
                return <span title={value}>{value}</span>
            }
        },
        {
            key: 'edit',
            name: 'Actions',
            minWidth: 64,
            maxWidth: 64,
            isResizable: false,
            isIconOnly: true,
            columnActionsMode: ColumnActionsMode.disabled,
            onRender: (item) => <SolutionActionButtonsView editedItem={item} callBack={onGetSolutionCallback} />
        }
    ] 

    const ownerColumns = [
        {
            key: 'name',
            name: 'Name',
            fieldName: 'name',
            minWidth: 150,
            maxWidth: 250,
            isRowHeader: false,
            isResizable: true,
            data: 'string'
        },
        {
            key: 'publisher',
            name: 'Publisher',
            fieldName: 'publisher',
            minWidth: 150,
            maxWidth: 250,
            isRowHeader: false,
            isResizable: true,
            data: 'string'
        },
        {
            key: 'description',
            name: 'Description',
            fieldName: 'description',
            minWidth: 150,
            maxWidth: 250,
            isRowHeader: false,
            isResizable: true,
            data: 'string'
        },
        {
            key: 'primaryOwner',
            name: 'Primary Owner',
            fieldName: 'primaryOwner',
            minWidth: 160,
            maxWidth: 200,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderUser(item.primaryOwner),
        },
        {
            key: 'secondaryOwner',
            name: 'Secondary Owner',
            fieldName: 'secondaryOwner',
            minWidth: 160,
            maxWidth: 200,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderUser(item.secondaryOwner),
        },
        {
            key: 'lastRecertificationDate',
            name: 'Last Recertification Date',
            fieldName: 'lastRecertificationDate',
            minWidth: 70,
            maxWidth: 100,
            isRowHeader: false,
            isResizable: true,
            data: 'number',
            onRender: (item) => {
                return <span>{renderDate(item.lastRecertificationDate)}</span>;
            }
        },
        {
            key: 'lastRecertifiedBy',
            name: 'Last Recertification By',
            fieldName: 'lastRecertifiedBy',
            minWidth: 160,
            maxWidth: 200,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderUser(item.lastRecertifiedBy),
        },
        {
            key: 'isManaged',
            name: 'Is Managed Solution',
            fieldName: 'isManagedSolution',
            minWidth: 60,
            maxWidth: 120,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderBoolean(item.isManagedSolution)
        },
        {
            key: 'environment',
            name: 'Environment',
            fieldName: 'environment',
            minWidth: 80,
            maxWidth: 150,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => <span>{item.environment.name}</span>
        },
        {
            key: 'edit',
            name: 'Actions',
            minWidth: 64,
            maxWidth: 64,
            isResizable: false,
            isIconOnly: true,
            columnActionsMode: ColumnActionsMode.disabled,
            onRender: (item) => <SolutionActionButtonsView editedItem={item} callBack={onGetSolutionCallback} filterBy={filterBy} />
        }
    ]

    const onGetSolutionCallback = async app => {
        const updatedItems = powerApps.map(x => (x.id === app.id) ? app : x);
        setPowerApps(updatedItems);
    }

    const exportApps = () => {
        const fields = isOwnerView
            ? [
                {
                    value: "name",
                    label: "Name"
                }, {
                    value: "publisher",
                    label: "Publisher"
                }, {
                    value: "description",
                    label: "Description"
                }, {
                    value: "primaryOwner",
                    label: "Primary Owner"
                }, {
                    value: "secondaryOwner",
                    label: "Secondary Owner"
                }, {
                    value: "lastRecertificationDate",
                    label: "Last Recertification Date"
                }, {
                    value: "lastRecertifiedBy",
                    label: "Last RecertificationBy"
                }, {
                    value: "isManagedSolution",
                    label: "Is Managed Solution"
                }, {
                    value: "environment",
                    label: "Environment"
                }] 
            : [
                {
                    value: "name",
                    label: "Name"
                }, {
                    value: "publisher",
                    label: "Publisher"
                }, {
                    value: "environment",
                    label: "Environment"
                }, {
                    value: "primaryOwner",
                    label: "Primary Owner"
                }, {
                    value: "secondaryOwner",
                    label: "Secondary Owner"
                }, {
                    value: "isManagedSolution",
                    label: "Is Managed Solution"
                }, {
                    value: "country",
                    label: "Country"
                }, {
                    value: "organization",
                    label: "Organization"
                }, {
                    value: "scope",
                    label: "Scope"
                }, {
                    value: "status",
                    label: "Status"
                }, {
                    value: "provisioningStatus",
                    label: "Technical Status"
                }, {
                    value: "ardoq",
                    label: "Business Capability"
                }
            ]

        setIsExporting(true);

        if (cancelSource.current) {
            cancelSource.current.cancel();
        }

        cancelSource.current = CancelToken.source();

        Api.get(getResultsUrl(true), {
            cancelToken: cancelSource.current.token
        })
        .then(res => res.data)
        .then(posts => {
            createAndDownloadCsv(posts, "powerAppSolutions", fields);
            setIsExporting(false);
        })
        .catch(err => {});

        return () => {
            cancelSource.current.cancel();
        }     
    }

    const getColumns = (columns) => {
 
        return columns.map(c => {
            c.isFiltered = (filters[c.fieldName] || '').length > 0;
            c.isSorted = orderBy.fieldName === c.fieldName;
            c.isSortedDescending = c.isSorted ? orderBy.isDescending : true;
            return c;
        });
    }
    
    const columns = getColumns(isOwnerView ? ownerColumns : initialColumns);
    const spinner = <Spinner style={{margin: '200px'}} size={SpinnerSize.large} />; 

    const statusTooltip =
        <div>
            Indicates the status of the app / flow / solution:
            <ul>
                <li>Active – The app or flow is currently available (suspended and turned off flows are also considered "Active" as they can be turned On by owners).</li>
                <li>Quarantined – The app or flow has been restricted due to policy violations, issues or lifecycle.</li>
                <li>Deleted – The app or flow has been removed and is no longer available.</li>
            </ul>
        </div> 
    
    const provisioningStatusTooltip =
        <div>
            Indicates the technical status of the app / flow / solution:
            <ul>
                <li>Ready – Fully functional and available for changes.</li>
                <li>Updating – Currently undergoing changes or updates.</li>
                <li>Failed – An error occurred during the last operation, it will be retried automatically.</li>
                <li>Permanently Failed – A critical failure occurred that cannot be automatically recovered, require action from Provisioner admin.</li>
                <li>No Access – Not accessible by users but still exist and possible to restore.</li>
                <li>Deleted – The item has been removed and is no longer available.</li>
                <li>Pending Approval – Awaiting approval decision.</li>
                <li>Partially Failed – Some components failed, but others may still be working.</li>
                <li>Expired – The item is no longer active due to recertification expiration.</li>
            </ul>
        </div>        

    const isWhitelistedFilterTooltip = 
        <div>
            Determines if solution/app/flow is included in lifecycle and visibility.
            <ul>
                <li><b>Yes</b> – This item is <b>whitelisted</b>. It is <b>hidden from end users</b> and <b>excluded from lifecycle management</b>.</li>
                <li><b>No</b> – This item is <b>not whitelisted</b>. It is <b>visible to end users</b> and <b>included in lifecycle management</b>.</li>
            </ul>
        </div>

    const filtersFields = 
        <div className="filters">
            <div className="cell">
                <TextFilter
                    fieldName='name'
                    fieldLabel='Name / ID'
                    onChange={onFilterChange}
                />
            </div>
            <div className="cell">
                <TextFilter
                    fieldName='publisher'
                    fieldLabel='Publisher'
                    onChange={onFilterChange}
                />
            </div>
            <div className="cell">
                <TextFilter
                    fieldName='environment'
                    fieldLabel='Environment'
                    onChange={onFilterChange}
                />
            </div>
            <div className="cell">
                <DropdownFilter
                    fieldName="isManagedSolution"
                    values={mapBooleanToFilterValues()}
                    title='Is Managed Solution'
                    onChange={onFilterChange}
                    noselection={handleNoSelection}
                />
            </div>
            <div className="cell">
                <TextFilter
                    fieldName='primaryOwner'
                    fieldLabel='Primary Owner'
                    onChange={onFilterChange}
                />
            </div>
            <div className="cell">
                <TextFilter
                    fieldName='secondaryOwner'
                    fieldLabel='Secondary Owner'
                    onChange={onFilterChange}
                />
            </div>
            <div className="cell">
                <DropdownTree
                    address = {'/Management?parent='}
                    fieldName='organization'
                    label = 'Busines Owner Organization'
                    multiselect = {false}
                    onChange = {onFilterChange}
                />
            </div>
            <div className="cell">
                <DropdownTree
                    address = {'/Geography?parent='}
                    fieldName='siteAudience'
                    label = 'Country'
                    multiselect = {true}
                    onChange = {onFilterChange}
                    includeCrossOrg={true}
                />
            </div>
            <div className="cell">
                <DropdownFilter
                    fieldName="scope"
                    values={mapEnumToFilterValues(PowerAppsScope)}
                    title='Scope'
                    onChange = {onFilterChange}
                    noselection = {handleNoSelection}
                    includeEmpty={true}
                />
            </div>
            <div className="cell">
                <DropdownFilter
                    fieldName="status"
                    values={mapEnumToFilterValues(PowerAppStatus)}
                    title={<span>Status <InfoTooltip content={statusTooltip} /></span>}
                    onChange = {onFilterChange}
                    noselection = {handleNoSelection}
                    includeSelectAll = {true}
                />
            </div>
            <div className="cell">
                <DropdownFilter
                    fieldName="provisioningStatus"
                    values={mapEnumToFilterValues(PowerAppProvisioningStatus)}
                    title={<span>Technical Status <InfoTooltip content={provisioningStatusTooltip} /></span>}
                    onChange = {onFilterChange}
                    noselection = {handleNoSelection}
                    includeSelectAll = {true}
                />
            </div>
            <div className="cell">
                <DropdownTree
                    address={'/Ardoq?parent='}
                    fieldName='ardoq'
                    label='Business Capability'
                    multiselect={true}
                    onChange={onFilterChange}
                    hideKey={true}
                />
            </div>
            <div className="cell">
                <TextFilter
                    fieldName='description'
                    fieldLabel='Description'
                    onChange={onFilterChange}
                    tooltip='Separate keywords that you want to search with comma (,).'
                />
            </div>
            {
                filterBy !== 'environmentOwner' &&
                <div className="cell">
                    <DropdownFilter
                        fieldName="isWhitelisted"
                        values={mapBooleanToFilterValues()}
                        title={<span>Is Whitelisted <InfoTooltip content={isWhitelistedFilterTooltip}/></span>}
                        onChange = {onFilterChange}
                        noselection = {handleNoSelection}
                    />
                </div>
            }
        </div>

    const ownerFiltersFields =
        <div className="filters">
            <div className="cell">
                <TextFilter
                    fieldName='name'
                    fieldLabel='Name / ID'
                    onChange={onFilterChange}
                />
            </div>
            <div className="cell">
                <TextFilter
                    fieldName='environment'
                    fieldLabel='Environment'
                    onChange={onFilterChange}
                />
            </div>
            <div className="cell">
                <TextFilter
                    fieldName='primaryOwner'
                    fieldLabel='Primary Owner'
                    onChange={onFilterChange}
                />
            </div>
            <div className="cell">
                <TextFilter
                    fieldName='secondaryOwner'
                    fieldLabel='Secondary Owner'
                    onChange={onFilterChange}
                />
            </div>
            <div className="cell">
                <DropdownFilter
                    fieldName="scope"
                    values={mapEnumToFilterValues(PowerAppsScope)}
                    title='Scope'
                    onChange = {onFilterChange}
                    noselection = {handleNoSelection}
                    includeEmpty={true}
                />
            </div>
        </div>

    const [sortedItems, groups] = getGroupedItems(powerApps, 'highestSolutionId', 'highestSolutionName');

    const groupedPagingInfo = allTotalResults > 0 &&
        <Label>
            <div>Showing solutions groups with pagination.</div>
            <div>Total solutions across all groups: {allTotalResults}.</div>
        </Label>

    return (
        <div>
            <div>
                {
                    isOwnerView
                        ? ownerFiltersFields
                        : filtersFields
                }
                <div style={{position:'relative', margin: '20px 0'}}>
                    <div>
                        <IconButton 
                            style={{color: 'rgb(96, 94, 92)', position: 'absolute'}} 
                            onClick={updateResults}
                            iconProps={{iconName: 'refresh'}} 
                        />
                        <PageNavi
                            pageNumPart = {setPageUrlFilter}
                            totalResults = {totalResults}
                            resultsOnPage = {onPage}
                            pageNum = {pageUrlFilter}
                            showTotalResults = {true}
                            additionalInfo={groupedPagingInfo}
                        />
                        
                        <Stack horizontal disableShrink>
                            <ResultsOnPage
                                onPage = {onPage}
                                onPageResults = {setOnPage}
                                />
                            <ExportButton 
                                onClick = {exportApps}
                                loading = {isExporting}
                                >                           
                            </ExportButton>
                        </Stack>                            
                    </div>
                </div>
            </div>
            <div>
                {
                    isLoading 
                    ? spinner                  
                    : <DetailsList
                        items={sortedItems}
                        columns={columns}
                        layoutMode={DetailsListLayoutMode.justified}
                        isHeaderVisible={true}
                        selectionMode={SelectionMode.none}
                        styles={{root: {width: '100%'}}}
                        onColumnHeaderClick={_onColumnClick}
                        constrainMode={ConstrainMode.unconstrained}
                        groups={groups}
                    />
                }
            </div>
        </div>
        )
    }


export default PowerPlatformSolutionsList;


// WEBPACK FOOTER //
// ./src/components/Administration/PowerPlatform/PowerPlatformSolutionsList.js